import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { createChatClient, defaultChatClient } from "../lib/chatApi";
import type { ChatClient } from "../lib/chatApi";
import type { BackendResponse, ChatMessage, ChatStatus, Route, GuidedAction, ChatDelta, ChatCandidates, ChatResultsDescription } from "../lib/types";
import { chatLogger } from "../lib/logger";
import { emitGlobalEvent } from "../lib/events";
import { EVENT_ADD_TO_CART, EVENT_ERROR, EVENT_NAVIGATE_PRODUCT, EVENT_STATUS, EVENT_DELTA, EVENT_CANDIDATES } from "../lib/events";
import { storeAttribution } from "../lib/attribution";
import type { AddToCartEventDetail, NavigateProductEventDetail } from "../lib/events";
import type { Product } from "../lib/mockData";
import { getRuntimeLocale, t as tRuntime } from "../lib/locale";

function applyTemplate(s: string, vars: Record<string, string | number>): string {
  let out = String(s || "");
  for (const [k, v] of Object.entries(vars)) {
    const needle = `{${k}}`;
    out = out.split(needle).join(String(v));
  }
  return out;
}

// Temporary hard-disable: skip backend recommendations and shopping list logic on add-to-cart
const DISABLE_ADD_TO_CART_FLOW = true;

interface ShoppingListState {
  pickingMessageId: string | null;
  selected: string[];
  confirmed: boolean;
  completed: Set<string>;
  currentIndex: number;
  searchIdToCategory: Record<string, string>;
}

interface CandidatesPaginationState {
  currentOffset: number;
  hasMore: boolean;
  isLoadingMore: boolean;
  totalCount: number;
}

interface BrowserMeta {
  queryTitle?: string;
  resultsCount?: number;
  phase?: "recommendation" | "question";
  resultsDescription?: string;
}

interface ChatContextValue {
  sessionId: string | null;
  messages: ChatMessage[];
  loading: boolean;
  cooldownUntil: number | null;
  browser: BrowserMeta | null;
  /** Browser-only UX: force show the free-text input even when not in prompt/answer/options flows. */
  forceShowInput: boolean;
  sendMessage: (text: string, isNewSession?: boolean) => Promise<void>;
  sendWithRoute: (text: string, route: Route, isNewSession?: boolean) => Promise<void>;
  /**
   * Run a chat request using the existing session + token refresh, but without mutating
   * the main chat transcript UI (messages/loading/browser meta). Intended for embedded
   * side-panels like ProductPanel.
   */
  askDetached: (
    text: string,
    route: Route,
    opts?: {
      signal?: AbortSignal;
      onStatus?: (s: ChatStatus) => void;
      onDelta?: (d: ChatDelta) => void;
    }
  ) => Promise<BackendResponse>;
  selectOption: (options: Array<{ index: number; label: string }>) => Promise<void>;
  resetChat: () => void;
  clearTurn: () => void;
  setCurrentProduct: (productId: string | null) => void;
  onAddToCart: (args: { product?: Partial<Product>; selectedVariant?: Record<string, string>; quantity?: number; searchId?: string }) => Promise<void>;
  onNavigateToProduct: (args: { product?: Partial<Product>; selectedVariant?: Record<string, string>; target?: string; searchId?: string }) => Promise<void>;
  onReadMore: (args: { product?: Partial<Product>; searchId?: string; metadata?: Record<string, unknown> }) => Promise<void>;
  onProductClick: () => void;
  // Shopping list state and actions
  shoppingList: ShoppingListState;
  startPicking: (messageId: string) => void;
  addPick: (label: string) => void;
  removePick: (label: string) => void;
  confirmPicks: () => void;
  markDoneByCategory: (name: string) => void;
  markDoneBySearchId: (searchId: string) => void;
  resetShoppingList: () => void;
  // Guided interactions
  staticPrefix: string | null;
  performGuidedAction: (action: GuidedAction) => void;
  lastSystemActions: GuidedAction[] | null;
  // Candidates pagination
  candidatesPagination: Record<string, CandidatesPaginationState>;
  fetchMoreCandidates: (messageId: string) => Promise<void>;
}

const ChatContext = createContext<ChatContextValue | undefined>(undefined);

function useProvideChat(chatClient: ChatClient, originUrl?: string): ChatContextValue {
  const [sessionId, setSessionId] = useState<string | null>(null);
  // Keep latest session id available to async/SSE callbacks without stale-closure issues.
  const sessionIdRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);
  const originUrlRef = React.useRef(originUrl);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null);
  const [status, setStatus] = useState<ChatStatus | null>(null);
  const [browser, setBrowser] = useState<BrowserMeta | null>(null);
  const [forceShowInput, setForceShowInput] = useState(false);
  /**
   * The current search identifier sent by the backend in the most recent "options" payload.
   * This must be echoed back when the user selects an option.
   */
  const [searchId, setSearchId] = useState<string | null>(null);
  const [currentProductId, setCurrentProductId] = useState<string | null>(() => {
    try {
      const v = (window as any)?.__WW__?.currentProductId;
      const s = typeof v === "string" ? v : (v != null ? String(v) : "");
      return s.trim().length > 0 ? s.trim() : null;
    } catch {
      return null;
    }
  });
  const abortRef = React.useRef<AbortController | null>(null);
  // Candidates pagination state - keyed by message ID
  const [candidatesPagination, setCandidatesPagination] = useState<Record<string, CandidatesPaginationState>>({});
  const addToCartTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  // Tracks the currently streaming assistant message (for token-delta streaming)
  const currentStreamRef = React.useRef<{ messageId: string | null; requestId?: string } | null>(null);
  const appendToStreamingMessage = React.useCallback((text: string) => {
    if (!text) return;
    const id = currentStreamRef.current?.messageId;
    if (!id) {
      const newId = crypto.randomUUID();
      setMessages((prev) => [...prev, { id: newId, text, isUser: false }]);
      currentStreamRef.current = { messageId: newId };
      return;
    }
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, text: (m.text || "") + text } : m)));
  }, []);
  const clearStreamingRef = React.useCallback((removeIfEmpty?: boolean) => {
    const id = currentStreamRef.current?.messageId;
    currentStreamRef.current = null;
    if (removeIfEmpty && id) {
      setMessages((prev) => {
        const msg = prev.find((m) => m.id === id);
        const isEmpty = !msg || typeof msg.text !== 'string' || msg.text.trim() === "";
        return isEmpty ? prev.filter((m) => m.id !== id) : prev;
      });
    }
  }, []);

  // Guided interactions: staging state and context
  const [staticPrefix, setStaticPrefix] = useState<string | null>(null);
  const [stagedRoute, setStagedRoute] = useState<Route | null>(null);
  const [stagedTemplate, setStagedTemplate] = useState<GuidedAction["template"] | null>(null);
  const [stagedSearchId, setStagedSearchId] = useState<string | null>(null);
  const [lastPromptQuestion, setLastPromptQuestion] = useState<string | null>(null);
  const [lastRecommendedProducts, setLastRecommendedProducts] = useState<string[]>([]);
  const [lastSearchId, setLastSearchId] = useState<string | null>(null);
  const [pendingAnswerActions, setPendingAnswerActions] = useState<GuidedAction[] | null>(null);
  const [lastSystemActions, setLastSystemActions] = useState<GuidedAction[] | null>(null);
  const pendingAnswerActionsRef = React.useRef<GuidedAction[] | null>(null);

  // Lightweight debug logger (prints to terminal/console)
  const debug = (...args: any[]) => {
    try { console.log('[Guided]', ...args); } catch {}
  };

  // Resolve product context at request-time. For Centra embeds this avoids stale values
  // after SPA product navigation by asking the adapter for a live product id per request.
  const getEffectiveCurrentProductId = React.useCallback((): string | undefined => {
    try {
      const liveResolver = (window as any)?.__WW_CENTRA_RESOLVE_PRODUCT_CONTEXT__;
      if (typeof liveResolver === "function") {
        const live = liveResolver();
        const raw =
          typeof live === "string"
            ? live
            : (live && typeof live === "object" && (live.productId ?? live.currentProductId) != null)
              ? String(live.productId ?? live.currentProductId)
              : "";
        const resolved = String(raw || "").trim();
        if (resolved.length > 0) return resolved;
        // Resolver is available but no product is detected now; avoid stale fallback.
        return undefined;
      }
    } catch {}
    if (currentProductId && currentProductId.trim().length > 0) return currentProductId;
    try {
      const v = (window as any)?.__WW__?.currentProductId;
      const s = typeof v === "string" ? v : (v != null ? String(v) : "");
      const out = s.trim();
      return out.length > 0 ? out : undefined;
    } catch {
      return undefined;
    }
  }, [currentProductId]);

  const getEffectiveCurrentSku = React.useCallback((): string | undefined => {
    try {
      const liveResolver = (window as any)?.__WW_CENTRA_RESOLVE_PRODUCT_CONTEXT__;
      if (typeof liveResolver === "function") {
        const live = liveResolver();
        const raw =
          typeof live === "string"
            ? ""
            : (live && typeof live === "object" && live.currentSku != null)
              ? String(live.currentSku)
              : "";
        const resolved = raw.trim();
        if (resolved.length > 0) return resolved;
        return undefined;
      }
    } catch {}
    try {
      const v = (window as any)?.__WW__?.currentSku;
      const s = typeof v === "string" ? v : (v != null ? String(v) : "");
      const out = s.trim();
      return out.length > 0 ? out : undefined;
    } catch {
      return undefined;
    }
  }, []);

  const getEffectiveChatContext = React.useCallback((): { currentProductId?: string; currentSku?: string } => {
    const currentSku = getEffectiveCurrentSku();
    if (currentSku) return { currentSku };
    const currentProductId = getEffectiveCurrentProductId();
    return currentProductId ? { currentProductId } : {};
  }, [getEffectiveCurrentProductId, getEffectiveCurrentSku]);

  // Shopping list: tracks ordered category picks and progress
  const [shoppingList, setShoppingList] = useState<ShoppingListState>({
    pickingMessageId: null,
    selected: [],
    confirmed: false,
    completed: new Set<string>(),
    currentIndex: 0,
    searchIdToCategory: {},
  });

  const persistSession = useCallback((id: string) => {
    const sid = typeof id === "string" ? id : String(id ?? "");
    if (!sid) return;
    // Avoid redundant updates; we never expect session_id to change mid-session.
    if (sessionIdRef.current === sid) return;
    if (sessionIdRef.current && sessionIdRef.current !== sid) {
      try { console.warn("[ChatContext] session_id changed unexpectedly", { from: sessionIdRef.current, to: sid }); } catch {}
      return;
    }
    sessionIdRef.current = sid;
    setSessionId(sid);
    chatLogger.setSession(sid);
  }, []);

  const maybePersistSessionFromSse = useCallback((evt: any) => {
    const sid = evt?.session_id;
    if (sid && !sessionIdRef.current) {
      persistSession(String(sid));
    }
  }, [persistSession]);

  // Convert SSE candidates into a persistent chat message and emit global event
  const handleCandidates = useCallback((payload: ChatCandidates) => {
    try {
      maybePersistSessionFromSse(payload as any);
      // Thread search_id for subsequent actions (Browser flow)
      if (payload?.search_id) {
        setSearchId(payload.search_id);
      }
      // Capture Browser extras from SSE (source of truth for results_count)
      const next: { queryTitle?: string; resultsCount?: number; phase?: "recommendation" | "question" } = {};
      if (typeof (payload as any)?.query_title === 'string') next.queryTitle = (payload as any).query_title as string;
      if (typeof (payload as any)?.results_count === 'number') next.resultsCount = (payload as any).results_count as number;
      if ((payload as any)?.phase === 'recommendation' || (payload as any)?.phase === 'question') next.phase = (payload as any).phase;
      if (Object.keys(next).length > 0) {
        setBrowser((prev) => ({ ...(prev || {}), ...next }));
      }

      const messageId = crypto.randomUUID();
      const candidates = Array.isArray(payload?.candidates) ? payload.candidates : [];
      const totalCount = typeof (payload as any)?.results_count === 'number' ? (payload as any).results_count : candidates.length;

      setMessages((prev) => [
        ...prev,
        {
          id: messageId,
          text: "",
          isUser: false,
          type: "candidates",
          candidates: candidates,
          search_id: payload?.search_id,
        },
      ]);

      // Initialize pagination state for this candidates message
      setCandidatesPagination((prev) => ({
        ...prev,
        [messageId]: {
          currentOffset: candidates.length, // Start offset after initial batch
          hasMore: candidates.length < totalCount, // Check if there are more candidates available
          isLoadingMore: false,
          totalCount: totalCount,
        },
      }));

      emitGlobalEvent(EVENT_CANDIDATES, {
        storeId: chatClient.config.storeId,
        sessionId,
        ...payload,
      });
    } catch {}
  }, [chatClient.config.storeId, sessionId, maybePersistSessionFromSse]);

  const handleResultsDescription = useCallback((payload: ChatResultsDescription) => {
    try { maybePersistSessionFromSse(payload as any); } catch {}
    const text = typeof payload?.text === "string" ? payload.text.trim() : "";
    setBrowser((prev) => {
      if (!prev && !text) return prev;
      const base: BrowserMeta = prev ? { ...prev } : {};
      return { ...base, resultsDescription: text || undefined };
    });
  }, [maybePersistSessionFromSse]);

  // Shopping list helpers
  const resetShoppingList = useCallback(() => {
    setShoppingList({
      pickingMessageId: null,
      selected: [],
      confirmed: false,
      completed: new Set<string>(),
      currentIndex: 0,
      searchIdToCategory: {},
    });
  }, []);

  const startPicking = useCallback((messageId: string) => {
    setShoppingList({
      pickingMessageId: messageId,
      selected: [],
      confirmed: false,
      completed: new Set<string>(),
      currentIndex: 0,
      searchIdToCategory: {},
    });
  }, []);

  const addPick = useCallback((label: string) => {
    setShoppingList((prev) => {
      if (prev.confirmed) return prev;
      if (prev.selected.includes(label)) return prev;
      return { ...prev, selected: [...prev.selected, label] };
    });
  }, []);

  const removePick = useCallback((label: string) => {
    setShoppingList((prev) => {
      if (prev.confirmed) return prev;
      const next = prev.selected.filter((l) => l !== label);
      return { ...prev, selected: next };
    });
  }, []);

  const confirmPicks = useCallback(() => {
    // Read current selection synchronously to avoid relying on async state updates
    const firstLeaf = shoppingList.selected[0] ?? null;
    if (shoppingList.confirmed || !firstLeaf) return;

    setShoppingList((prev) => ({ ...prev, confirmed: true, currentIndex: 0 }));

    (async () => {
      try {
        setLoading(true);
        setStatus(null);
        // Cancel any in-flight request
        abortRef.current?.abort();
        abortRef.current = new AbortController();
        // Map first selected label -> 0-based option_index from the original ordered_options message
        const pickingMsg = messages.find((m) => m.id === shoppingList.pickingMessageId);
        const opts = pickingMsg?.options ?? [];
        const matched = opts.find((o) => o.label === firstLeaf);
        const idx = (matched?.index ?? opts.findIndex((o) => o.label === firstLeaf));

        if (sessionId && searchId && typeof idx === 'number' && idx >= 0) {
          // Use the same endpoint/shape as single-option questions
          const data = await chatClient.postSelectOption(
            sessionId,
            searchId,
            [idx],
            abortRef.current.signal,
            {
              onStatus: (s) => {
                try { maybePersistSessionFromSse(s as any); } catch {}
                setStatus(s);
                try {
                  emitGlobalEvent(EVENT_STATUS, {
                    storeId: chatClient.config.storeId,
                    sessionId,
                    ...s,
                  });
                } catch {}
              },
              onCandidates: handleCandidates,
              onResultsDescription: handleResultsDescription,
            }
          );
          handleBackendResponse(data);
        } else {
          // Fallback to leaf-based call if index/searchId can't be resolved
          const currentContext = getEffectiveChatContext();
          const data = await chatClient.postChat(
            sessionId,
            "",
            currentContext.currentProductId,
            abortRef.current.signal,
            {
              currentSku: currentContext.currentSku,
              onStatus: (s) => {
                try { maybePersistSessionFromSse(s as any); } catch {}
                setStatus(s);
                try {
                  emitGlobalEvent(EVENT_STATUS, {
                    storeId: chatClient.config.storeId,
                    sessionId,
                    ...s,
                  });
                } catch {}
              },
              leaf: firstLeaf as string,
              onCandidates: handleCandidates,
              onResultsDescription: handleResultsDescription,
            }
          );
          handleBackendResponse(data);
        }
      } catch (err) {
        const error = err as any as Error & { code?: string; retryAfterMs?: number };
        if ((error as any).name === 'AbortError') {
          return;
        }
        if ((error as any).code === 'rate_limited') {
          const ms = (error as any).retryAfterMs ?? 30000;
          setCooldownUntil(Date.now() + ms);
          const locale = getRuntimeLocale();
          setMessages((prev) => [
            ...prev,
            {
              id: crypto.randomUUID(),
              text: applyTemplate(tRuntime("common.rate_limited_wait", locale), { seconds: Math.ceil(ms / 1000) }),
              isUser: false,
            },
          ]);
          setLoading(false);
          return;
        }
        handleBackendResponse({
          session_id: sessionId ?? "",
          type: "error",
          error: error.message,
        });
      } finally {
        setLoading(false);
        setStatus(null);
      }
    })();
  }, [shoppingList.selected, shoppingList.confirmed, chatClient, sessionId, currentProductId, messages, searchId, handleResultsDescription]);

  const markDoneByCategory = useCallback((name: string) => {
    setShoppingList((prev) => {
      const completed = new Set(prev.completed);
      completed.add(name);
      // Advance currentIndex to next incomplete item
      let nextIndex = prev.selected.length;
      for (let i = 0; i < prev.selected.length; i++) {
        if (!completed.has(prev.selected[i])) { nextIndex = i; break; }
      }
      return { ...prev, completed, currentIndex: nextIndex };
    });
  }, []);

  const markDoneBySearchId = useCallback((sid: string) => {
    setShoppingList((prev) => {
      const cat = prev.searchIdToCategory[sid];
      if (!cat) return prev;
      const completed = new Set(prev.completed);
      completed.add(cat);
      let nextIndex = prev.selected.length;
      for (let i = 0; i < prev.selected.length; i++) {
        if (!completed.has(prev.selected[i])) { nextIndex = i; break; }
      }
      return { ...prev, completed, currentIndex: nextIndex };
    });
  }, []);

  const handleBackendResponse = (data: BackendResponse) => {
    debug('handleBackendResponse:type', data.type, { hasPending: Boolean(pendingAnswerActions), lastSystemActionsLen: lastSystemActions?.length });
    if (data.session_id && data.session_id !== sessionId) {
      persistSession(data.session_id);
    }

    switch (data.type) {
      case "prompt": {
        debug('prompt:question', data.question);
        const locale = getRuntimeLocale();
        const actions: GuidedAction[] = [
          {
            id: crypto.randomUUID(),
            label: tRuntime("browser.explain_question", locale),
            mode: "send",
            route: "QUESTION",
            template: "PROMPT_EXPLAIN",
          },
          {
            id: crypto.randomUUID(),
            label: tRuntime("prompt.not_important", locale),
            mode: "send",
            route: "ANSWER",
            template: "PROMPT_NOT_IMPORTANT",
          },
        ];
        setLastPromptQuestion(data.question ?? "");
        // Store an invariant key (not translated text) so logic can compare reliably.
        setStaticPrefix("prompt.prefix_answer");
        setLastSystemActions(actions);
        debug('prompt:setLastSystemActions', { actionsLen: actions.length, staticPrefix: "prompt.prefix_answer" });
        const streamingId = currentStreamRef.current?.messageId;
        if (streamingId) {
          setMessages((prev) =>
            prev.map((m) => (m.id === streamingId ? { ...m, text: data.question ?? "", type: "prompt" } : m))
          );
          clearStreamingRef(false);
        } else {
          setMessages((prev) => [
            ...prev,
            {
              id: crypto.randomUUID(),
              text: data.question ?? "",
              isUser: false,
              type: "prompt",
            },
          ]);
        }
        chatLogger.logChat(data.question ?? "", false, { type: "prompt" });
        break;
      }
      case "options": {
        // Persist the search ID so we can include it when sending the selected option.
        if (data.search_id) {
          setSearchId(data.search_id);
        }
        // Enable the "Answer" route behavior for free-form input, matching prompt behavior
        setStaticPrefix("prompt.prefix_answer");
        const streamingId = currentStreamRef.current?.messageId;
        if (streamingId) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === streamingId
                ? { ...m, text: data.question ?? "", type: "options", options: data.options ?? [], search_id: data.search_id }
                : m
            )
          );
          clearStreamingRef(false);
        } else {
          setMessages((prev) => [
            ...prev,
            {
              id: crypto.randomUUID(),
              text: data.question ?? "",
              isUser: false,
              type: "options",
              options: data.options ?? [],
              search_id: data.search_id,
            },
          ]);
        }
        chatLogger.logChat(data.question ?? "", false, { 
          type: "options",
          options: data.options,
          search_id: data.search_id,
        });
        break;
      }
      case "ordered_options": {
        if (data.search_id) {
          setSearchId(data.search_id);
        }
        const streamingId = currentStreamRef.current?.messageId;
        if (streamingId) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === streamingId
                ? { ...m, text: data.question ?? "", type: "ordered_options", options: data.options ?? [], search_id: data.search_id }
                : m
            )
          );
          clearStreamingRef(false);
          // Enable multi-pick flow on the reconciled streaming message
          startPicking(streamingId);
        } else {
          const msgId = crypto.randomUUID();
          setMessages((prev) => [
            ...prev,
            {
              id: msgId,
              text: data.question ?? "",
              isUser: false,
              type: "ordered_options",
              options: data.options ?? [],
              search_id: data.search_id,
            },
          ]);
          // Enable multi-pick flow for this message
          startPicking(msgId);
        }
        chatLogger.logChat(data.question ?? "", false, {
          type: "ordered_options",
          options: data.options,
          search_id: data.search_id,
        });
        break;
      }
      case "result": {
        const customerMessage = data.recommendation?.customer_message ?? "";
        const productNames: string[] = Array.isArray(data.recommendation?.recommended_products)
          ? data.recommendation!.recommended_products.map((p: any) => p?.name).filter((n: any) => typeof n === 'string')
          : [];
        debug('result:products', productNames);
        const locale = getRuntimeLocale();
        const actions: GuidedAction[] = [
          {
            id: crypto.randomUUID(),
            label: tRuntime("guided.refine", locale),
            mode: "stage",
            route: "REFINE",
            template: "REFINE",
            search_id: data.search_id,
          },
          {
            id: crypto.randomUUID(),
            label: tRuntime("guided.new_search", locale),
            mode: "stage",
            route: "NEW_SEARCH",
            template: "NEW_SEARCH",
          },
          {
            id: crypto.randomUUID(),
            label: tRuntime("guided.ask_products", locale),
            mode: "stage",
            route: "QUESTION",
            template: "QUESTION_PRODUCTS",
          },
        ];
        setLastRecommendedProducts(productNames);
        setLastSearchId(data.search_id ?? null);
        // Thread search_id from final result as well for Browser flows
        if (data.search_id) {
          setSearchId(data.search_id);
        }
        // Update Browser extras from final only for title/phase (not resultsCount)
        {
          const next: { queryTitle?: string; phase?: "recommendation" | "question" } = {};
          if (typeof (data as any)?.query_title === 'string') next.queryTitle = (data as any).query_title as string;
          if ((data as any)?.phase === 'recommendation' || (data as any)?.phase === 'question') next.phase = (data as any).phase;
          if (Object.keys(next).length > 0) {
            setBrowser((prev) => ({ ...(prev || {}), ...next } as any));
          }
        }
        // Inject proceed action only if shopping list is active and a next item exists
        if (shoppingList.confirmed) {
          // Resolve current category mapped to this result, or fall back to current index
          const currentCategory = data.search_id && shoppingList.searchIdToCategory[data.search_id]
            ? shoppingList.searchIdToCategory[data.search_id]
            : shoppingList.selected[shoppingList.currentIndex];
          if (currentCategory) {
            const completedAfter = new Set(shoppingList.completed);
            completedAfter.add(currentCategory);
            const nextCategory = shoppingList.selected.find((n) => !completedAfter.has(n));
            if (nextCategory) {
              const locale = getRuntimeLocale();
              actions.push({
                id: crypto.randomUUID(),
                label: applyTemplate(tRuntime("browser.continue_to_category", locale), { category: nextCategory }),
                mode: 'send',
                route: 'ANSWER',
                template: 'NEXT_CATEGORY',
                search_id: data.search_id,
                meta: { searchId: data.search_id ?? undefined },
              });
            }
          }
        }
        setLastSystemActions(actions);
        debug('result:setLastSystemActions', { actionsLen: actions.length, searchId: data.search_id });
        // If shopping list is active and result has a search_id, map it to the current target category
        if (shoppingList.confirmed && data.search_id) {
          setShoppingList((prev) => {
            if (prev.searchIdToCategory[data.search_id!]) return prev;
            const target = prev.selected[Math.min(prev.currentIndex, prev.selected.length - 1)];
            if (!target) return prev;
            return {
              ...prev,
              searchIdToCategory: { ...prev.searchIdToCategory, [data.search_id!]: target },
            };
          });
        }
        const streamingId = currentStreamRef.current?.messageId;
        if (streamingId) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === streamingId
                ? { ...m, text: customerMessage, isUser: false, type: "result", recommendation: data.recommendation, search_id: data.search_id, actions }
                : m
            )
          );
          clearStreamingRef(false);
        } else {
          setMessages((prev) => [
            ...prev,
            {
              id: crypto.randomUUID(),
              text: customerMessage,
              isUser: false,
              type: "result",
              recommendation: data.recommendation,
              search_id: data.search_id,
              actions,
            },
          ]);
        }
        chatLogger.logChat(customerMessage, false, {
          type: "result",
          recommendation: data.recommendation,
        });
        break;
      }
      case "answer": {
        const actions = pendingAnswerActionsRef.current ?? pendingAnswerActions ?? undefined;
        debug('answer:pendingActions', { pendingLen: actions?.length ?? 0, lastSystemActionsLen: lastSystemActions?.length });
        const streamingId = currentStreamRef.current?.messageId;
        if (streamingId) {
          // Reconcile streamed message with authoritative final answer
          setMessages((prev) => prev.map((m) => (m.id === streamingId ? { ...m, text: data.answer ?? "", type: "answer", ...(actions ? { actions } : {}) } : m)));
          clearStreamingRef(false);
        } else {
          // No streaming happened; append a new assistant message as before
          setMessages((prev) => [
            ...prev,
            { id: crypto.randomUUID(), text: data.answer ?? "", isUser: false, type: "answer", ...(actions ? { actions } : {}) },
          ]);
        }
        if (actions && (actions as GuidedAction[]).length > 0) {
          setLastSystemActions(actions as GuidedAction[]);
          debug('answer:setLastSystemActionsFromPending', { len: (actions as GuidedAction[]).length });
        }
        setPendingAnswerActions(null);
        pendingAnswerActionsRef.current = null;
        debug('answer:clearedPending');
        chatLogger.logChat(data.answer ?? "", false, { type: "answer" });
        break;
      }
      case "done": {
        // Non-destructive final: do not append transcript content; just allow free-text input again.
        setForceShowInput(true);
        // Match options/prompt behavior for free-text input routing.
        setStaticPrefix("prompt.prefix_answer");
        // If we created an empty streaming message during this request, remove it.
        clearStreamingRef(true);
        break;
      }
      case "error":
      default: {
        // Non-answer final: clear any dangling empty stream before adding payload
        clearStreamingRef(true);
        const errorText = data.error ?? "error";
        const statusMatch = /\b(\d{3})\b/.exec(errorText || "");
        const status = statusMatch ? parseInt(statusMatch[1], 10) : undefined;
        const isAuthFailure = status === 401 || status === 403 || /auth/i.test(errorText || "");
        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            text: errorText,
            isUser: false,
          },
          ...(isAuthFailure
            ? [{
                id: crypto.randomUUID(),
                text: tRuntime("common.auth_failed_reload", getRuntimeLocale()),
                isUser: false,
              }]
            : []),
        ]);
        chatLogger.logError(errorText || "Unknown error", { 
          type: "backend_error",
          response: data 
        });
        if (isAuthFailure) {
          emitGlobalEvent(EVENT_ERROR, { code: 'auth_failed', status, message: errorText });
        }
        break;
      }
    }
  };

  // Proceed to the next shopping list category without adding to cart or emitting UI messages
  const proceedToNextCategory = useCallback(async ({ searchId: providedSearchId }: { searchId?: string } = {}) => {
    // Only applicable when a shopping list is active
    if (!shoppingList.confirmed) return;

    const effectiveSearchId = providedSearchId ?? undefined;
    let currentCategory: string | undefined;
    if (effectiveSearchId && shoppingList.searchIdToCategory[effectiveSearchId]) {
      currentCategory = shoppingList.searchIdToCategory[effectiveSearchId];
    } else {
      currentCategory = shoppingList.selected[shoppingList.currentIndex];
    }
    if (!currentCategory) return;

    // Mark current as done if not already completed
    if (!shoppingList.completed.has(currentCategory)) {
      markDoneByCategory(currentCategory);
    }

    // Determine next incomplete category
    const completedAfter = new Set(shoppingList.completed);
    completedAfter.add(currentCategory);
    const nextCategory = shoppingList.selected.find((n) => !completedAfter.has(n));
    if (!nextCategory) return; // nothing left; do not show a button in such cases

    // Start next leaf search silently (no badges or helper messages)
    try {
      setLoading(true);
      setStatus(null);
      abortRef.current?.abort();
      abortRef.current = new AbortController();
      // Start a fresh stream for this request
      currentStreamRef.current = null;
      const currentContext = getEffectiveChatContext();
      const data = await chatClient.postChat(
        sessionId,
        "",
        currentContext.currentProductId,
        abortRef.current.signal,
        {
          currentSku: currentContext.currentSku,
          onStatus: (s) => {
            try { maybePersistSessionFromSse(s as any); } catch {}
            setStatus(s);
            try {
              emitGlobalEvent(EVENT_STATUS, { storeId: chatClient.config.storeId, sessionId, ...s });
            } catch {}
          },
          onDelta: (d: ChatDelta) => {
            try { maybePersistSessionFromSse(d as any); } catch {}
            const chunk = (d?.delta as string) ?? ((d as any)?.token as string) ?? "";
            appendToStreamingMessage(chunk);
          },
          leaf: nextCategory,
          onCandidates: handleCandidates,
        }
      );
      handleBackendResponse(data);
    } finally {
      setLoading(false);
      setStatus(null);
    }
  }, [shoppingList, markDoneByCategory, chatClient, sessionId, currentProductId, maybePersistSessionFromSse]);

  // Build a composed message for staged templates. User sees only their typed text; backend receives the composed text.
  const buildStagedMessage = useCallback((template: GuidedAction["template"] | null, userText: string): string => {
    switch (template) {
      case "REFINE":
        return `The user wants to refine the search that includes these recommended products: ${lastRecommendedProducts.join(', ')}. Their refinement is this: ${userText}`;
      case "NEW_SEARCH":
        return `the user wants to make a new product search. This is their new search: ${userText}`;
      case "QUESTION_PRODUCTS":
        return `The user is asking a question about these products: ${lastRecommendedProducts.join(', ')}. This is the users question: ${userText}`;
      case "PROMPT_EXPLAIN":
        return `Please explain more clearly what you mean by this question: ${lastPromptQuestion ?? ''}, include more info about the options, how to choose between them and why it is important.`;
      case "PROMPT_NOT_IMPORTANT":
        return `User does not care about the attribute related to this question: ${lastPromptQuestion ?? ''}, set as null`;
      default:
        return userText;
    }
  }, [lastRecommendedProducts, lastPromptQuestion]);

  const askDetached = useCallback(async (text: string, route: Route, opts?: { signal?: AbortSignal; onStatus?: (s: ChatStatus) => void; onDelta?: (d: ChatDelta) => void; }): Promise<BackendResponse> => {
    const trimmed = (text || "").trim();
    if (!trimmed) {
      return {
        session_id: sessionId ?? "",
        type: "error",
        error: "empty_message",
      };
    }

    // Use current session (if any). Detached calls should not force a new session.
    const currentSessionId = sessionId;

    // Include origin_page only for the first request in a session (same semantics as sendMessage).
    const postOpts: {
      onStatus?: (s: ChatStatus) => void;
      onDelta?: (d: ChatDelta) => void;
      route?: Route;
      origin_page?: string;
    } = {
      onStatus: (s) => {
        try { maybePersistSessionFromSse(s as any); } catch {}
        opts?.onStatus?.(s);
      },
      onDelta: (d) => {
        try { maybePersistSessionFromSse(d as any); } catch {}
        opts?.onDelta?.(d);
      },
      route,
    };
    if (!currentSessionId && originUrlRef.current) {
      postOpts.origin_page = originUrlRef.current;
      originUrlRef.current = undefined;
    }

    const currentContext = getEffectiveChatContext();
    const data = await chatClient.postChat(
      currentSessionId,
      trimmed,
      currentContext.currentProductId,
      opts?.signal,
      {
        ...postOpts,
        currentSku: currentContext.currentSku,
      }
    );

    // If detached interaction is the first touch, persist the backend session id so we truly
    // "reuse the same session" for subsequent interactions (and backend history is consistent).
    if (!sessionId && data?.session_id) {
      persistSession(data.session_id);
    }

    return data;
  }, [chatClient, getEffectiveChatContext, originUrlRef, persistSession, sessionId, maybePersistSessionFromSse]);

  const sendMessage = useCallback(async (text: string, isNewSession: boolean = false) => {
    // Starting a new turn clears any prior "done => show input" override.
    setForceShowInput(false);
    // Capture and clear any static input prefix as soon as user presses send
    const prevStaticPrefix = staticPrefix;
    if (staticPrefix) {
      setStaticPrefix(null);
    }
    // If user is answering a prompt, hide the prompt actions now
    if (prevStaticPrefix === "prompt.prefix_answer") {
      setLastSystemActions(null);
    }

    if (cooldownUntil && Date.now() < cooldownUntil) {
      const secs = Math.ceil((cooldownUntil - Date.now()) / 1000);
      const locale = getRuntimeLocale();
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          text: applyTemplate(tRuntime("common.too_fast_wait", locale), { seconds: secs }),
          isUser: false,
        },
      ]);
      return;
    }
    const message: ChatMessage = {
      id: crypto.randomUUID(),
      text,
      isUser: true,
    };
    setMessages((prev) => [...prev, message]);
    // Update Browser heading: append to existing title for cumulative behavior
    setBrowser((prev) => {
      const existingTitle = prev?.queryTitle;
      const newTitle = existingTitle ? `${existingTitle}, ${text}` : text;
      return { ...(prev || {}), queryTitle: newTitle, resultsDescription: undefined };
    });
    
    setLoading(true);
    setStatus(null);
    try {
      // Cancel any in-flight request
      abortRef.current?.abort();
      abortRef.current = new AbortController();
      const currentSessionId = isNewSession ? null : sessionId;
      // Optimistic default status immediately
      setStatus({ title: tRuntime("common.thinking", getRuntimeLocale()), status: undefined });
      // Determine route and message for backend (user-visible text stays as typed)
      let backendMessage = text;
      let backendRoute: Route | undefined = undefined;
      if (stagedRoute) {
        backendRoute = stagedRoute;
        backendMessage = buildStagedMessage(stagedTemplate, text);
        debug('sendMessage:staged', { route: backendRoute, template: stagedTemplate, userText: text, composed: backendMessage });
        // Clear staging after consuming
        setStagedRoute(null);
        setStagedTemplate(null);
        setStagedSearchId(null);
        // Repeat only for product-question flow (QUESTION_PRODUCTS)
        if (
          backendRoute === "QUESTION" &&
          stagedTemplate === "QUESTION_PRODUCTS" &&
          lastSystemActions && lastSystemActions.length > 0
        ) {
          setPendingAnswerActions(lastSystemActions);
          pendingAnswerActionsRef.current = lastSystemActions;
          debug('sendMessage:setPendingFromLastSystemActions', { len: lastSystemActions.length, template: stagedTemplate });
        } else if (backendRoute === "QUESTION") {
          debug('sendMessage:questionNoRepeat', { template: stagedTemplate });
        }
      } else if (prevStaticPrefix === "prompt.prefix_answer") {
        backendRoute = "ANSWER";
        debug('sendMessage:freeformAnswer', { route: backendRoute, userText: text });
      }
      // Default to NEW_SEARCH on the very first message of a session when no route is set
      if (!currentSessionId && !backendRoute) {
        backendRoute = "NEW_SEARCH";
      }
      // Log user message with routing metadata
      chatLogger.logChat(text, true, { route: backendRoute, stagedTemplate });
      
      // Include origin_page on first message in session
      const opts: {
        onStatus?: (s: ChatStatus) => void;
        onDelta?: (d: ChatDelta) => void;
        route?: Route;
        origin_page?: string;
        search_id?: string;
      } = {
        onStatus: (s) => {
          try { maybePersistSessionFromSse(s as any); } catch {}
          setStatus(s);
          // Keep loader visible throughout SSE lifecycle
          setLoading(true);
          try {
            emitGlobalEvent(EVENT_STATUS, {
              storeId: chatClient.config.storeId,
              sessionId,
              ...s,
            });
          } catch {}
        },
        onDelta: (d) => {
          try { maybePersistSessionFromSse(d as any); } catch {}
          const chunk = (d?.delta as string) ?? ((d as any)?.token as string) ?? "";
          appendToStreamingMessage(chunk);
          try {
            emitGlobalEvent(EVENT_DELTA, { storeId: chatClient.config.storeId, sessionId, ...d, delta: chunk });
          } catch {}
        },
        route: backendRoute,
        search_id: stagedSearchId ?? undefined,
      };
      
      // Add origin page only on first send (when no sessionId exists)
      if (!currentSessionId && originUrlRef.current) {
        opts.origin_page = originUrlRef.current;
        // Clear after using once
        originUrlRef.current = undefined;
      }
      
      // Start a fresh stream for this request
      currentStreamRef.current = null;
      const currentContext = getEffectiveChatContext();
      const data = await chatClient.postChat(
        currentSessionId,
        backendMessage,
        currentContext.currentProductId,
        abortRef.current.signal,
        {
          ...opts,
          currentSku: currentContext.currentSku,
          onCandidates: handleCandidates,
          onResultsDescription: handleResultsDescription,
        }
      );
      debug('sendMessage:response', { type: data?.type });
      if (isNewSession && data.session_id) {
        persistSession(data.session_id);
      }
      handleBackendResponse(data);
      // End of lifecycle: hide loader
      setLoading(false);
      setStatus(null);
    } catch (err) {
      const error = err as any as Error & { code?: string; retryAfterMs?: number };
      if ((error as any).name === 'AbortError') {
        // Swallow cancellation
        return;
      }
      if ((error as any).code === 'rate_limited') {
        const ms = (error as any).retryAfterMs ?? 30000;
        setCooldownUntil(Date.now() + ms);
        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            text: applyTemplate(tRuntime("common.rate_limited_wait", getRuntimeLocale()), { seconds: Math.ceil(ms / 1000) }),
            isUser: false,
          },
        ]);
        setLoading(false);
        return;
      }
      chatLogger.logError(error, { 
        type: "api_error",
        message: text 
      });
      handleBackendResponse({
        session_id: sessionId ?? "",
        type: "error",
        error: error.message,
      });
      if ((error as any)?.status === 401 || (error as any)?.status === 403) {
        emitGlobalEvent(EVENT_ERROR, { code: 'auth_failed', status: (error as any).status, message: error.message });
      }
      // End of lifecycle on error as well
      setLoading(false);
      setStatus(null);
    }
  }, [
    sessionId,
    getEffectiveChatContext,
    chatClient,
    cooldownUntil,
    stagedRoute,
    stagedTemplate,
    staticPrefix,
    buildStagedMessage,
    lastSystemActions,
    handleResultsDescription,
  ]);

  // Debug: trace loading and related state transitions centrally
  React.useEffect(() => {
    const last = messages[messages.length - 1];
    const lastType = last ? ((last as any).type ?? (last.isUser ? 'user' : 'assistant')) : null;
    try {
      console.log('[ChatContext] loading changed:', loading, {
        statusTitle: status?.title,
        statusState: status?.status,
        messagesLen: messages.length,
        lastMsgType: lastType,
        cooldownUntil,
        browserMeta: browser,
        streamingActive: Boolean(currentStreamRef.current?.messageId),
      });
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  React.useEffect(() => {
    try { console.log('[ChatContext] status updated:', status); } catch {}
  }, [status]);

  const sendWithRoute = useCallback(async (text: string, route: Route, isNewSession: boolean = false) => {
    // Starting a new turn clears any prior "done => show input" override.
    setForceShowInput(false);
    const trimmed = (text || "").trim();
    if (!trimmed) return;
    if (cooldownUntil && Date.now() < cooldownUntil) {
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          text: applyTemplate(tRuntime("common.too_fast_wait", getRuntimeLocale()), { seconds: Math.ceil((cooldownUntil - Date.now()) / 1000) }),
          isUser: false,
        },
      ]);
      return;
    }

    const message: ChatMessage = { id: crypto.randomUUID(), text: trimmed, isUser: true };
    setMessages((prev) => [...prev, message]);
    // Update Browser heading: append to existing title for cumulative behavior
    setBrowser((prev) => {
      const existingTitle = prev?.queryTitle;
      const newTitle = existingTitle ? `${existingTitle}, ${trimmed}` : trimmed;
      return { ...(prev || {}), queryTitle: newTitle, resultsDescription: undefined };
    });

    setLoading(true);
    setStatus(null);
    try {
      abortRef.current?.abort();
      abortRef.current = new AbortController();
      const currentSessionId = isNewSession ? null : sessionId;
      setStatus({ title: tRuntime("common.thinking", getRuntimeLocale()), status: undefined });

      chatLogger.logChat(trimmed, true, { route });

      const opts: {
        onStatus?: (s: ChatStatus) => void;
        onDelta?: (d: ChatDelta) => void;
        route?: Route;
        origin_page?: string;
        search_id?: string;
      } = {
        onStatus: (s) => {
          try { maybePersistSessionFromSse(s as any); } catch {}
          setStatus(s);
          // Keep loader visible throughout SSE lifecycle
          setLoading(true);
          try {
            emitGlobalEvent(EVENT_STATUS, { storeId: chatClient.config.storeId, sessionId, ...s });
          } catch {}
        },
        onDelta: (d) => {
          try { maybePersistSessionFromSse(d as any); } catch {}
          const chunk = (d?.delta as string) ?? ((d as any)?.token as string) ?? "";
          appendToStreamingMessage(chunk);
          try {
            emitGlobalEvent(EVENT_DELTA, { storeId: chatClient.config.storeId, sessionId, ...d, delta: chunk });
          } catch {}
        },
        route,
        search_id: undefined,
      };

      if (!currentSessionId && originUrlRef.current) {
        opts.origin_page = originUrlRef.current;
        originUrlRef.current = undefined;
      }

      // Start a fresh stream for this request
      currentStreamRef.current = null;
      const currentContext = getEffectiveChatContext();
      const data = await chatClient.postChat(
        currentSessionId,
        trimmed,
        currentContext.currentProductId,
        abortRef.current.signal,
          {
            ...opts,
            currentSku: currentContext.currentSku,
            onCandidates: handleCandidates,
            onResultsDescription: handleResultsDescription,
          }
      );
      if (isNewSession && data.session_id) {
        persistSession(data.session_id);
      }
      handleBackendResponse(data);
      // End of lifecycle: hide loader
      setLoading(false);
      setStatus(null);
    } catch (err) {
      const error = err as any as Error & { code?: string; retryAfterMs?: number };
      if ((error as any).name === 'AbortError') {
        return;
      }
      if ((error as any).code === 'rate_limited') {
        const ms = (error as any).retryAfterMs ?? 30000;
        setCooldownUntil(Date.now() + ms);
        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            text: applyTemplate(tRuntime("common.rate_limited_wait", getRuntimeLocale()), { seconds: Math.ceil(ms / 1000) }),
            isUser: false,
          },
        ]);
        setLoading(false);
        return;
      }
      chatLogger.logError(error, { type: "api_error", message: trimmed });
      handleBackendResponse({
        session_id: sessionId ?? "",
        type: "error",
        error: error.message,
      });
      if ((error as any)?.status === 401 || (error as any)?.status === 403) {
        emitGlobalEvent(EVENT_ERROR, { code: 'auth_failed', status: (error as any).status, message: error.message });
      }
      // End of lifecycle on error as well
      setLoading(false);
      setStatus(null);
    }
  }, [sessionId, chatClient, cooldownUntil, handleResultsDescription, getEffectiveChatContext]);

  const performGuidedAction = useCallback((action: GuidedAction) => {
    if (!action) return;
    if (action.mode === 'stage') {
      const locale = getRuntimeLocale();
      setStagedRoute(action.route);
      setStagedTemplate(action.template);
      // Set static prefixes exactly as specified
      switch (action.template) {
        case 'REFINE':
          setStaticPrefix("prefix.refine");
          // Capture the search id associated with the clicked refine action
          setStagedSearchId(action.search_id ?? null);
          break;
        case 'NEW_SEARCH':
          setStaticPrefix("prefix.new_search");
          setStagedSearchId(null);
          break;
        case 'QUESTION_PRODUCTS':
          setStaticPrefix("prefix.question");
          setStagedSearchId(null);
          break;
        default:
          break;
      }
      debug('performGuidedAction:stage', { route: action.route, template: action.template });
      return;
    }
    // This action will trigger a new backend turn; clear any prior "done => show input" override.
    setForceShowInput(false);
    // mode === 'send'
    // Special case: proceed to next category without sending a message or emitting cart events
    if (action.template === 'NEXT_CATEGORY') {
      proceedToNextCategory({ searchId: action.meta?.searchId ?? action.search_id });
      return;
    }
    const composed = buildStagedMessage(action.template, '');
    debug('performGuidedAction:send', { route: action.route, template: action.template, composed });
    // If user marks prompt as not important, clear prompt UI immediately
    if (action.template === 'PROMPT_NOT_IMPORTANT') {
      setStaticPrefix(null);
      setLastSystemActions(null);
    }
    // Immediate send with route; do not include any static prefix
    (async () => {
      try {
        setLoading(true);
        setStatus(null);
        // Cancel any in-flight request
        abortRef.current?.abort();
        abortRef.current = new AbortController();
        const currentSessionId = sessionId;
        setStatus({ title: tRuntime("common.thinking", getRuntimeLocale()), status: undefined });

        // Optimistically show what user "sent"? We keep UX consistent: send a user bubble with the label text
        setMessages((prev) => [
          ...prev,
          { id: crypto.randomUUID(), text: action.label, isUser: true }
        ]);
        chatLogger.logChat(action.label, true, { type: 'guided_action', route: action.route, template: action.template });

        // Repeat only for product-question flow (QUESTION_PRODUCTS)
        if (
          action.route === 'QUESTION' &&
          action.template === 'QUESTION_PRODUCTS' &&
          lastSystemActions && lastSystemActions.length > 0
        ) {
          setPendingAnswerActions(lastSystemActions);
          pendingAnswerActionsRef.current = lastSystemActions;
        } else if (action.route === 'QUESTION') {
          debug('performGuidedAction:questionNoRepeat', { template: action.template });
        }

        // Start a fresh stream for this request
        currentStreamRef.current = null;
        const currentContext = getEffectiveChatContext();
        const data = await chatClient.postChat(
          currentSessionId,
          composed,
          currentContext.currentProductId,
          abortRef.current.signal,
          {
            currentSku: currentContext.currentSku,
            onStatus: (s) => {
              try { maybePersistSessionFromSse(s as any); } catch {}
              setStatus(s);
              try {
                emitGlobalEvent(EVENT_STATUS, { storeId: chatClient.config.storeId, sessionId, ...s });
              } catch {}
            },
            onDelta: (d: ChatDelta) => {
              try { maybePersistSessionFromSse(d as any); } catch {}
              const chunk = (d?.delta as string) ?? ((d as any)?.token as string) ?? "";
              appendToStreamingMessage(chunk);
            try {
              emitGlobalEvent(EVENT_DELTA, { storeId: chatClient.config.storeId, sessionId, ...d, delta: chunk });
            } catch {}
            },
            route: action.route,
            onCandidates: handleCandidates,
            onResultsDescription: handleResultsDescription,
          }
        );
        debug('performGuidedAction:response', { type: (data as any)?.type });
        handleBackendResponse(data);
      } catch (err) {
        if ((err as any)?.name === 'AbortError') return;
        chatLogger.logError(err as Error, { type: 'guided_action_error', action: action.label });
        setMessages((prev) => [
          ...prev,
          { id: crypto.randomUUID(), text: 'Sorry, there was an error. Please try again.', isUser: false },
        ]);
      } finally {
        setLoading(false);
        setStatus(null);
      }
    })();
  }, [buildStagedMessage, sessionId, chatClient, lastSystemActions, proceedToNextCategory, handleResultsDescription, getEffectiveChatContext]);

  const selectOption = useCallback(async (options: Array<{ index: number; label: string }>) => {
    // Starting a new turn clears any prior "done => show input" override.
    setForceShowInput(false);
    // Clear any pending answer prefix when an option is explicitly selected
    setStaticPrefix(null);
    if (cooldownUntil && Date.now() < cooldownUntil) return;
    if (!options || options.length === 0) return;
    const labels = options.map((opt) => opt.label);
    const optionText = labels.join(", ");
    chatLogger.logChat(optionText, true, { type: "option_selection" });
    // Require a session to proceed. If searchId is missing, fall back to posting the label(s) as a normal message.
    if (!sessionId) return;
    // Update Browser heading: append to existing title for cumulative behavior
    setBrowser((prev) => {
      const existingTitle = prev?.queryTitle;
      const newTitle = existingTitle ? `${existingTitle}, ${optionText}` : optionText;
      return { ...(prev || {}), queryTitle: newTitle, resultsDescription: undefined };
    });

    setLoading(true);
    setStatus(null);
    try {
      abortRef.current?.abort();
      abortRef.current = new AbortController();

      let data: BackendResponse;
      if (searchId) {
        const uniqueIndices = Array.from(new Set(options.map((opt) => opt.index)));
        // Normal path: correlate selection with specific options set
        // Start a fresh stream for this request
        currentStreamRef.current = null;
        data = await chatClient.postSelectOption(
          sessionId,
          searchId,
          uniqueIndices,
          abortRef.current.signal,
          {
            onStatus: (s) => {
              try { maybePersistSessionFromSse(s as any); } catch {}
              setStatus(s);
              try {
                emitGlobalEvent(EVENT_STATUS, {
                  storeId: chatClient.config.storeId,
                  sessionId,
                  ...s,
                });
              } catch {}
            },
            onDelta: (d: ChatDelta) => {
              try { maybePersistSessionFromSse(d as any); } catch {}
              const chunk = (d?.delta as string) ?? ((d as any)?.token as string) ?? "";
              appendToStreamingMessage(chunk);
              try {
                emitGlobalEvent(EVENT_DELTA, { storeId: chatClient.config.storeId, sessionId, ...d, delta: chunk });
              } catch {}
            },
            onCandidates: handleCandidates,
            onResultsDescription: handleResultsDescription,
          }
        );
      } else {
        // Fallback path: treat selection as a regular message to continue the flow
        // Start a fresh stream for this request
        currentStreamRef.current = null;
        const currentContext = getEffectiveChatContext();
        data = await chatClient.postChat(
          sessionId,
          optionText,
          currentContext.currentProductId,
          abortRef.current.signal,
          {
            currentSku: currentContext.currentSku,
            onStatus: (s) => {
              try { maybePersistSessionFromSse(s as any); } catch {}
              setStatus(s);
              try {
                emitGlobalEvent(EVENT_STATUS, {
                  storeId: chatClient.config.storeId,
                  sessionId,
                  ...s,
                });
              } catch {}
            },
            onDelta: (d: ChatDelta) => {
              try { maybePersistSessionFromSse(d as any); } catch {}
              const chunk = (d?.delta as string) ?? ((d as any)?.token as string) ?? "";
              appendToStreamingMessage(chunk);
              try {
                emitGlobalEvent(EVENT_DELTA, { storeId: chatClient.config.storeId, sessionId, ...d, delta: chunk });
              } catch {}
            },
            onCandidates: handleCandidates,
            onResultsDescription: handleResultsDescription,
          }
        );
      }
      handleBackendResponse(data);
    } catch (error) {
      if ((error as any)?.code === 'rate_limited') {
        const ms = (error as any).retryAfterMs ?? 30000;
        setCooldownUntil(Date.now() + ms);
        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            text: applyTemplate(tRuntime("common.rate_limited_wait", getRuntimeLocale()), { seconds: Math.ceil(ms / 1000) }),
            isUser: false,
          },
        ]);
        setLoading(false);
        return;
      }
      if ((error as any).name === 'AbortError') return;
      console.error("Error sending option:", error);
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          text: "Sorry, there was an error processing your selection. Please try again.",
          isUser: false,
        },
      ]);
      if ((error as any)?.status === 401 || (error as any)?.status === 403) {
        emitGlobalEvent(EVENT_ERROR, { code: 'auth_failed', status: (error as any).status, message: 'Authorization failed after retry.' });
      }
    } finally {
      setLoading(false);
      setStatus(null);
    }
  }, [sessionId, searchId, handleBackendResponse, chatClient, handleResultsDescription, getEffectiveChatContext]);

  React.useEffect(() => {
    return () => {
      if (addToCartTimerRef.current) {
        clearTimeout(addToCartTimerRef.current);
        addToCartTimerRef.current = null;
      }
    };
  }, []);

  const resetChat = () => {
    if (sessionId) {
      chatLogger.log({
        type: "SYSTEM",
        message: "Chat session reset",
        metadata: { sessionId }
      });
    }
    setForceShowInput(false);
    setSessionId(null);
    setMessages([]);
    setSearchId(null);
    setBrowser(null);
    resetShoppingList();
    // Clear guided interaction state
    setStaticPrefix(null);
    setStagedRoute(null);
    setStagedTemplate(null);
    setLastPromptQuestion(null);
    setLastRecommendedProducts([]);
    setLastSearchId(null);
    setPendingAnswerActions(null);
    setLastSystemActions(null);
    // Clear candidates pagination state
    setCandidatesPagination({});
  };

  const clearTurn = useCallback(() => {
    setForceShowInput(false);
    // Abort any in-flight request and clear a dangling empty streaming message
    try { abortRef.current?.abort(); } catch {}
    clearStreamingRef(true);

    // Clear current transcript (messages) only
    setMessages([]);
    // Also clear any active status to avoid duplicate status-driven UI on next turn
    setStatus(null);

    // Hide any guided input prefix (e.g., "Svar:") so input disappears
    setStaticPrefix(null);

    // Clear Browser header fields while preserving queryTitle for cumulative behavior
    setBrowser((prev) => {
      if (!prev) return prev;
      return { ...prev, resultsCount: undefined, resultsDescription: undefined };
    });
  }, [clearStreamingRef]);

  const setCurrentProduct = useCallback((productId: string | null) => {
    setCurrentProductId(productId);
  }, []);

  const onAddToCart = useCallback(async ({ product, selectedVariant, quantity, searchId: providedSearchId }: { product?: Partial<Product>; selectedVariant?: Record<string, string>; quantity?: number; searchId?: string }) => {
    const effectiveSearchId = providedSearchId ?? searchId ?? undefined;
    const canPostToBackend = Boolean(sessionId && effectiveSearchId && product?.id && selectedVariant);

    // Build event detail up-front for both fast and fallback paths
    const detail: AddToCartEventDetail = {
      storeId: chatClient.config.storeId,
      sessionId: sessionId ?? undefined,
      searchId: effectiveSearchId,
      product: product ? {
        id: product.id,
        title: product.title,
        images: product.images,
        rawImageObjects: (product as any).rawImageObjects,
        price: product.price,
        productHandle: (product as any).productHandle,
        productUrl: (product as any).productUrl,
        description: product.description,
      } : undefined,
      selectedVariant,
      quantity,
    };

    // If disabled, emit storefront event only and exit early
    if (DISABLE_ADD_TO_CART_FLOW) {
      try { emitGlobalEvent(EVENT_ADD_TO_CART, detail); } catch {}
      chatLogger.log({
        type: "SYSTEM",
        message: "on-add-to-cart emitted (flow disabled)",
        metadata: { productId: product?.id, selectedVariant }
      });
      return;
    }

    // Clear any previous fallback timer (safety)
    if (addToCartTimerRef.current) {
      clearTimeout(addToCartTimerRef.current);
      addToCartTimerRef.current = null;
    }

    // Ensure single emission per invocation
    let emitted = false;
    const emitOnce = () => {
      if (emitted) return;
      emitted = true;
      emitGlobalEvent(EVENT_ADD_TO_CART, detail);
      chatLogger.log({
        type: "SYSTEM",
        message: "on-add-to-cart emitted",
        metadata: { productId: product?.id, selectedVariant }
      });
    };

    // Schedule 5s fallback in case backend path is slow
    addToCartTimerRef.current = setTimeout(() => {
      emitOnce();
      addToCartTimerRef.current = null;
    }, 5000);

    try {
      // Resolve category context only if list is active
      const isListActive = shoppingList.confirmed;
      let category: string | undefined;
      if (isListActive) {
        if (effectiveSearchId && shoppingList.searchIdToCategory[effectiveSearchId]) {
          category = shoppingList.searchIdToCategory[effectiveSearchId];
        } else {
          category = shoppingList.selected[shoppingList.currentIndex];
        }
      }

      if (isListActive && category) {
        // If already completed, just emit storefront/cart and exit
        if (shoppingList.completed.has(category)) {
          if (addToCartTimerRef.current) {
            clearTimeout(addToCartTimerRef.current);
            addToCartTimerRef.current = null;
          }
          emitOnce();
          return;
        }

        // Mark done and emit UI feedback
        markDoneByCategory(category);
        setMessages((prev) => [
          ...prev,
          { id: crypto.randomUUID(), text: `${category} tillagda`, isUser: false, ui: { variant: 'badge' } },
        ]);

        // Determine next category based on derived completion state
        const completedAfter = new Set(shoppingList.completed);
        completedAfter.add(category);
        const nextCategory = shoppingList.selected.find((n) => !completedAfter.has(n));

        if (nextCategory) {
          setMessages((prev) => [
            ...prev,
            { id: crypto.randomUUID(), text: `Bra val! Låt oss fortsätta till ${nextCategory}`, isUser: false },
          ]);
          // Start next leaf search
          try {
            setLoading(true);
            setStatus(null);
            abortRef.current?.abort();
            abortRef.current = new AbortController();
            // Start a fresh stream for this request
            currentStreamRef.current = null;
            const currentContext = getEffectiveChatContext();
            const data = await chatClient.postChat(
              sessionId,
              "",
              currentContext.currentProductId,
              abortRef.current.signal,
              {
                currentSku: currentContext.currentSku,
                onStatus: (s) => {
                  try { maybePersistSessionFromSse(s as any); } catch {}
                  setStatus(s);
                  try {
                    emitGlobalEvent(EVENT_STATUS, { storeId: chatClient.config.storeId, sessionId, ...s });
                  } catch {}
                },
                onDelta: (d: ChatDelta) => {
                  try { maybePersistSessionFromSse(d as any); } catch {}
                  const chunk = (d?.delta as string) ?? ((d as any)?.token as string) ?? "";
                  appendToStreamingMessage(chunk);
                  try {
                    emitGlobalEvent(EVENT_DELTA, { storeId: chatClient.config.storeId, sessionId, ...d, delta: chunk });
                  } catch {}
                },
                leaf: nextCategory,
              onResultsDescription: handleResultsDescription,
              }
            );
            handleBackendResponse(data);
          } finally {
            setLoading(false);
            setStatus(null);
          }
          // Storefront/UI should reflect cart update immediately
          if (addToCartTimerRef.current) {
            clearTimeout(addToCartTimerRef.current);
            addToCartTimerRef.current = null;
          }
          emitOnce();
          return;
        }

        // Last item in the list: trigger recommendations path now
        if (canPostToBackend) {
          // Show spinner text immediately (badge already shown above)
          setMessages((prev) => [
            ...prev,
            { id: crypto.randomUUID(), text: `Bra val! Ge mig en liten stund...`, isUser: false },
          ]);

          abortRef.current?.abort();
          abortRef.current = new AbortController();
          try {
            setLoading(true);
            setStatus({ title: tRuntime("common.thinking", getRuntimeLocale()), status: undefined });
            // Start a fresh stream for this request
            currentStreamRef.current = null;
            const response = await chatClient.postOnAddToCart({
              session_id: sessionId as string,
              product_ids: [String(product!.id)],
              search_id: effectiveSearchId,
              selected_variant_ids: {},
            }, abortRef.current.signal, {
              onStatus: (s) => {
                try { maybePersistSessionFromSse(s as any); } catch {}
                setStatus(s);
                try {
                  emitGlobalEvent(EVENT_STATUS, { storeId: chatClient.config.storeId, sessionId, ...s });
                } catch {}
              },
              onDelta: (d: ChatDelta) => {
                try { maybePersistSessionFromSse(d as any); } catch {}
                const chunk = (d?.delta as string) ?? ((d as any)?.token as string) ?? "";
                appendToStreamingMessage(chunk);
                try {
                  emitGlobalEvent(EVENT_DELTA, { storeId: chatClient.config.storeId, sessionId, ...d, delta: chunk });
                } catch {}
              },
            onCandidates: handleCandidates,
            onResultsDescription: handleResultsDescription,
            });
            if (response && typeof response === 'object' && (response as any).type) {
              try {
                handleBackendResponse(response as BackendResponse);
              } catch {}
            }
          } finally {
            setLoading(false);
            setStatus(null);
          }
        }
        if (addToCartTimerRef.current) {
          clearTimeout(addToCartTimerRef.current);
          addToCartTimerRef.current = null;
        }
        emitOnce();
        return;
      } else if (canPostToBackend) {
        // Normal flow: show UX feedback instantly, then trigger recommendations
        const statusCategory = (effectiveSearchId && shoppingList.searchIdToCategory[effectiveSearchId]) ? shoppingList.searchIdToCategory[effectiveSearchId] : undefined;
        setMessages((prev) => [
          ...prev,
          { id: crypto.randomUUID(), text: `${statusCategory ? statusCategory + ' ' : ''}tillagda`, isUser: false, ui: { variant: 'badge' } },
          { id: crypto.randomUUID(), text: `Bra val! Ge mig en liten stund...`, isUser: false },
        ]);

        abortRef.current?.abort();
        abortRef.current = new AbortController();
        try {
          setLoading(true);
          setStatus({ title: tRuntime("common.thinking", getRuntimeLocale()), status: undefined });
          // Start a fresh stream for this request
          currentStreamRef.current = null;
          const response = await chatClient.postOnAddToCart({
            session_id: sessionId as string,
            product_ids: [String(product!.id)],
            search_id: effectiveSearchId,
            selected_variant_ids: {},
          }, abortRef.current.signal, {
            onStatus: (s) => {
              try { maybePersistSessionFromSse(s as any); } catch {}
              setStatus(s);
              try {
                emitGlobalEvent(EVENT_STATUS, { storeId: chatClient.config.storeId, sessionId, ...s });
              } catch {}
            },
            onDelta: (d: ChatDelta) => {
              try { maybePersistSessionFromSse(d as any); } catch {}
              const chunk = (d?.delta as string) ?? ((d as any)?.token as string) ?? "";
              appendToStreamingMessage(chunk);
              try {
                emitGlobalEvent(EVENT_DELTA, { storeId: chatClient.config.storeId, sessionId, ...d, delta: chunk });
              } catch {}
            },
            onCandidates: handleCandidates,
            onResultsDescription: handleResultsDescription,
          });
          if (response && typeof response === 'object' && (response as any).type) {
            try {
              handleBackendResponse(response as BackendResponse);
            } catch {}
          }
        } finally {
          setLoading(false);
          setStatus(null);
        }
        // After normal path, emit storefront/cart events now
        if (addToCartTimerRef.current) {
          clearTimeout(addToCartTimerRef.current);
          addToCartTimerRef.current = null;
        }
        emitOnce();
      } else {
        // Even if we cannot post to backend (missing ids), still emit storefront/cart events
        if (addToCartTimerRef.current) {
          clearTimeout(addToCartTimerRef.current);
          addToCartTimerRef.current = null;
        }
        emitOnce();
      }
    } catch (error) {
      if ((error as any)?.code === 'rate_limited') {
        const ms = (error as any).retryAfterMs ?? 30000;
        setCooldownUntil(Date.now() + ms);
        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            text: applyTemplate(tRuntime("common.rate_limited_wait", getRuntimeLocale()), { seconds: Math.ceil(ms / 1000) }),
            isUser: false,
          },
        ]);
        // Keep the fallback timer so add-to-cart still triggers at 5s if not already emitted
        return;
      }
      chatLogger.logError(error as Error, {
        type: "on_add_to_cart_error",
        productId: product?.id as string | undefined,
        selectedVariant,
      });
      if ((error as any)?.status === 401 || (error as any)?.status === 403) {
        emitGlobalEvent(EVENT_ERROR, { code: 'auth_failed', status: (error as any).status, message: 'Authorization failed after retry.' });
      }
      // Do not clear the fallback; allow it to fire if needed
    }
  }, [sessionId, searchId, chatClient, shoppingList, markDoneByCategory, handleResultsDescription, getEffectiveChatContext]);


  const onNavigateToProduct = useCallback(async ({ product, selectedVariant, target, searchId: providedSearchId }: { product?: Partial<Product>; selectedVariant?: Record<string, string>; target?: string; searchId?: string }) => {
    const effectiveSearchId = providedSearchId ?? searchId ?? undefined;
    const detail: NavigateProductEventDetail = {
      storeId: chatClient.config.storeId,
      sessionId: sessionId ?? undefined,
      searchId: effectiveSearchId,
      product: product ? {
        id: product.id,
        title: product.title,
        images: product.images,
        rawImageObjects: (product as any).rawImageObjects,
        price: product.price,
        productHandle: (product as any).productHandle,
        productUrl: (product as any).productUrl,
        description: product.description,
      } : undefined,
      selectedVariant,
      target,
    };
    emitGlobalEvent(EVENT_NAVIGATE_PRODUCT, detail);
  }, [sessionId, searchId, chatClient]);

  const onReadMore = useCallback(async ({ product, searchId: providedSearchId, metadata }: { product?: Partial<Product>; searchId?: string; metadata?: Record<string, unknown> }) => {
    const sid = sessionIdRef.current;
    // Skip tracking if no session (user hasn't interacted with chat yet)
    if (!sid) {
      chatLogger.log({
        type: "SYSTEM",
        message: "onReadMore: skipped (no session)",
        metadata: { productId: product?.id }
      });
      return;
    }

    const effectiveSearchId = providedSearchId ?? searchId ?? undefined;

    try {
      // Persist attribution data for read-more clicks
      try {
        storeAttribution({
          storeId: chatClient.config.storeId,
          source: "browser_read_more",
          productId: product?.id,
        });
        try { localStorage.setItem('pako_session_id', String(sid)); } catch {}
      } catch {
        // Swallow storage errors to avoid impacting UX
      }

      // Fire-and-forget tracking call to backend
      await chatClient.postReadMore({
        session_id: sid,
        search_id: effectiveSearchId,
        product_id: product?.id ? String(product.id) : undefined,
        metadata: {
          source: "product_panel",
          component: "read-more",
          ...metadata,
        },
      });

      chatLogger.log({
        type: "SYSTEM",
        message: "onReadMore: tracking sent",
        metadata: { 
          productId: product?.id,
          searchId: effectiveSearchId,
        }
      });
    } catch (error) {
      // Silently log errors - tracking should never break UX
      chatLogger.log({
        type: "SYSTEM",
        message: "onReadMore: tracking failed",
        metadata: { 
          productId: product?.id,
          error: (error as Error)?.message,
        }
      });
    }
  }, [searchId, chatClient]);

  const onProductClick = useCallback(() => {
    const sid = sessionIdRef.current;
    if (!sid) return; // Silent skip if no session
    chatClient.postProductClick(sid).catch(() => {}); // Fire-and-forget
  }, [chatClient]);

  return {
    sessionId,
    messages,
    loading,
    cooldownUntil,
    browser,
    forceShowInput,
    // Expose status via context by widening the type with a cast to preserve external API
    ...( { status } as any ),
    sendMessage,
    sendWithRoute,
    askDetached,
    selectOption,
    resetChat,
    clearTurn,
    setCurrentProduct,
    onAddToCart,
    onNavigateToProduct,
    onReadMore,
    onProductClick,
    // Shopping list exposure
    shoppingList,
    startPicking,
    addPick,
    removePick,
    confirmPicks,
    markDoneByCategory,
    markDoneBySearchId,
    resetShoppingList,
    // Guided interactions
    staticPrefix,
    performGuidedAction,
    lastSystemActions,
    // Candidates pagination
    candidatesPagination,
    fetchMoreCandidates: useCallback(async (messageId: string) => {
      const paginationState = candidatesPagination[messageId];
      if (!paginationState || !paginationState.hasMore || paginationState.isLoadingMore) {
        return;
      }

      // Find the candidates message
      const message = messages.find(m => m.id === messageId);
      if (!message || message.type !== 'candidates') {
        return;
      }

      // Set loading state
      setCandidatesPagination(prev => ({
        ...prev,
        [messageId]: { ...paginationState, isLoadingMore: true }
      }));

      try {
        const response = await chatClient.postCandidatesPage({
          session_id: sessionId || '',
          search_id: message.search_id,
          offset: paginationState.currentOffset,
          limit: 24, // Always fetch 24 at a time as per requirements
        });

        // Update the candidates message with new candidates
        setMessages(prev => prev.map(m =>
          m.id === messageId && m.type === 'candidates'
            ? { ...m, candidates: [...(m.candidates || []), ...(response.candidates || [])] }
            : m
        ));

        // Update pagination state
        setCandidatesPagination(prev => ({
          ...prev,
          [messageId]: {
            currentOffset: response.offset + response.candidates.length,
            hasMore: response.has_more,
            isLoadingMore: false,
            totalCount: response.total,
          }
        }));
      } catch (error) {
        console.error('[ChatContext] fetchMoreCandidates error:', error);
        // Reset loading state on error
        setCandidatesPagination(prev => ({
          ...prev,
          [messageId]: { ...paginationState, isLoadingMore: false }
        }));
      }
    }, [candidatesPagination, messages, sessionId, chatClient]),
  };
}

export const ChatProvider = ({ children, client, config, originUrl }: { children: React.ReactNode; client?: ChatClient; config?: { apiBase: string; storeId: string; token?: string }; originUrl?: string }) => {
  const effectiveClient = useMemo<ChatClient>(() => {
    if (client) return client;
    if (config) return createChatClient(config);
    return defaultChatClient;
  }, [client, config]);

  const chat = useProvideChat(effectiveClient, originUrl);
  React.useEffect(() => {
    return () => {
      // Abort inflight on unmount
      // eslint-disable-next-line react-hooks/exhaustive-deps
      (chat as any)?.abortRef?.current?.abort?.();
    };
  }, []);
  return <ChatContext.Provider value={chat}>{children}</ChatContext.Provider>;
};

export const useChat = () => {
  const ctx = useContext(ChatContext);
  if (!ctx) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return ctx;
}; 